import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { SupportVideo } from './support.data';
import SupportVideoCard from './SupportVideoCard';

interface SupportVideoSectionProps {
  videos: SupportVideo[];
  onSelectVideo: (video: SupportVideo) => void;
  title?: string;
  description?: string;
}

const SupportVideoSection: React.FC<SupportVideoSectionProps> = ({
  videos,
  onSelectVideo,
  title = 'Getting Started',
  description = 'Learn the basics of creating with Pixle.',
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateControls = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    setCanScrollPrev(track.scrollLeft > 4);
    setCanScrollNext(track.scrollLeft < maxScrollLeft - 4);
  }, []);

  useEffect(() => {
    updateControls();
    window.addEventListener('resize', updateControls);
    return () => window.removeEventListener('resize', updateControls);
  }, [updateControls, videos.length]);

  const scrollByPage = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({
      left: direction * track.clientWidth * 0.9,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  // Per spec §25: only render categories that contain videos.
  // V1 treats the whole list as the Getting Started section.
  if (videos.length === 0) return null;

  const showControls = canScrollPrev || canScrollNext;

  return (
    <section className="support-block" aria-labelledby="support-getting-started">
      <div className="support-video-head">
        <div className="support-video-head-copy">
          <h2 id="support-getting-started" className="support-block-title">
            {title}
          </h2>
          <p className="support-block-subtitle">{description}</p>
        </div>
        {showControls && (
          <div className="support-slider-controls">
            <button
              type="button"
              className="support-slider-button"
              onClick={() => scrollByPage(-1)}
              disabled={!canScrollPrev}
              aria-label="Scroll tutorials backward"
            >
              <ChevronLeft size={20} strokeWidth={2.2} />
            </button>
            <button
              type="button"
              className="support-slider-button"
              onClick={() => scrollByPage(1)}
              disabled={!canScrollNext}
              aria-label="Scroll tutorials forward"
            >
              <ChevronRight size={20} strokeWidth={2.2} />
            </button>
          </div>
        )}
      </div>
      <div
        ref={trackRef}
        className="support-video-track"
        role="region"
        aria-label="Tutorial videos"
        tabIndex={0}
        onScroll={updateControls}
      >
        {videos.map((video) => (
          <SupportVideoCard key={video.id} video={video} onSelect={onSelectVideo} />
        ))}
      </div>
    </section>
  );
};

export default SupportVideoSection;
